"use client";

/**
 * ContentQuizCta — the "take the quiz" CTA used inside SEO content
 * (archetype pages + blog articles).
 *
 * It funnels organic readers into the EXISTING quiz funnel without touching
 * it: a <Link> to `/quiz?ev=<variantId>&ea=<angle>` so /api/quiz/start can
 * attribute the quiz_started event to the content entry point (entry_variant_id
 * + entry_angle), and it fires the existing `hero_cta_clicked` event on click
 * (same event the homepage hero fires) with a content-specific cta_position.
 *
 * Constraints mirrored from /api/quiz/start:
 *   - entry_variant_id (ev): 1–8 chars  → keep variantId short ("blog", "mp-arch")
 *   - entry_angle (ea): one of ANGLES   → typed as Angle
 *
 * Two visual styles:
 *   - `band` (default): a teal card with heading + button, for mid/end article.
 *   - `inline`: a single button, for tighter slots.
 */

import Link from "next/link";
import { track } from "@/lib/analytics";
import type { Angle } from "@/lib/content/homepage-variants";

interface Props {
  /** entry_variant_id sent to /quiz — MUST be ≤ 8 chars. Defaults to "blog". */
  variantId?: string;
  /** entry_angle sent to /quiz — one of ANGLES. Defaults to "money-leaks". */
  angle?: Angle;
  /** Stable analytics label for where this CTA sits (e.g. "blog_mid"). */
  placement: string;
  /** Optional heading shown above the button in the `band` style. */
  heading?: string;
  /** Optional supporting line under the heading. */
  subheading?: string;
  /** Button label. Defaults to "Take the free 5-minute quiz". */
  label?: string;
  style?: "band" | "inline";
  className?: string;
}

function buildHref(variantId: string, angle: Angle): string {
  const sp = new URLSearchParams({ ev: variantId, ea: angle });
  return `/quiz?${sp.toString()}`;
}

export function ContentQuizCta({
  variantId = "blog",
  angle = "money-leaks",
  placement,
  heading,
  subheading,
  label = "Take the free 5-minute quiz",
  style = "band",
  className,
}: Props) {
  const href = buildHref(variantId, angle);

  function fire() {
    track("hero_cta_clicked", {
      variant_id: variantId,
      angle,
      cta_id: placement,
      cta_position: placement,
      source: "content",
    });
  }

  if (style === "inline") {
    return (
      <Link
        href={href}
        onClick={fire}
        data-analytics-event="hero_cta_clicked"
        data-cta-position={placement}
        className={`inline-flex items-center rounded-full bg-teal px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-teal-hover ${className ?? ""}`}
      >
        {label}
      </Link>
    );
  }

  return (
    <div
      className={`not-prose my-8 rounded-2xl border border-teal/30 bg-teal/5 p-6 text-center sm:p-8 ${className ?? ""}`}
    >
      <h3 className="font-s5-display text-[22px] font-extrabold leading-tight text-s5-ink md:text-[26px]">
        {heading ?? "Which money type are you?"}
      </h3>
      <p className="mx-auto mt-2 max-w-[48ch] text-[15px] leading-[1.6] text-s5-ink-muted">
        {subheading ??
          "Take the free 5-minute quiz to find your money archetype and see where your money quietly slips away each year."}
      </p>
      <Link
        href={href}
        onClick={fire}
        data-analytics-event="hero_cta_clicked"
        data-cta-position={placement}
        className="mt-5 inline-flex items-center rounded-full bg-teal px-7 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-teal-hover"
      >
        {label}
      </Link>
    </div>
  );
}
